/* eslint-disable no-restricted-imports */
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useOpenDialog } from '../../../../../routing/ModalRoute';
import { DIALOGS } from '../../../../../routing/constants';
import { useIsMobile } from '../../../../../utils/getIsMobile';
import { useTranslation } from '../../../../../utils/useTranslation';
import { Skeleton } from '../../../../feedback/Skeleton';
import { IconKebab } from '../../../../icon/IconKebab';
import { IconNewTab } from '../../../../icon/IconNewTab';
import { Button } from '../../../../input/Button';
import { Box } from '../../../../layout/Box';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Flex } from '../../../../layout/Flex';
import { Menu, MenuItem } from '../../../../navigation/Menu';
import { Typography } from '../../../../typography/Typography';
import { getAuthUrl } from '../../../../users/DirectAuthorizationDialog/utils/getAuthUrl';
import { useCampaignActions } from './hooks/useCampaignActions';
import { ButtonWrapper, StartFlex } from './styles';
import { getStatusButtonText } from './utils/getStatusButtonText';
import { openInfo } from './utils/openInfo';
import type { ContentPromotionActionsProps } from './types';

export const ContentPromotionActions: React.FC<ContentPromotionActionsProps> = ({
    directCompanies,
    isLoading,
    isAuthorized,
}) => {
    const t = useTranslation();
    const openDialog = useOpenDialog();

    const company = directCompanies?.[0];
    const status = getStatusButtonText(company);
    const { processCampaign } = useCampaignActions(company);

    const isMobile = useIsMobile();
    const masterReportLink = company?.links?.mol;

    const onStartClick = useCallback(() => {
        if (isAuthorized) {
            window.location.href = getAuthUrl();

            return;
        }

        openDialog(DIALOGS.DIRECT_AUTHORIZATION);
    }, [isAuthorized, openDialog]);

    if (isLoading) {
        return (
            <Flex direction="row" mt={1} spacing={1}>
                <Skeleton variant="rect" width={102} height={40} borderRadius="999px" />
                <Skeleton variant="rect" width={154} height={40} borderRadius="999px" />
                <Skeleton variant="circular" width={40} height={40} />
            </Flex>
        );
    }

    if (!company) {
        return (
            <StartFlex>
                <Button
                    size="l"
                    onClick={onStartClick}
                    dataTestId={DataTestId.CONTENT_PROMOTION_START}
                >
                    {t('Начать продвижение')}
                </Button>
            </StartFlex>
        );
    }

    return (
        <StartFlex>
            <ButtonWrapper>
                {status ? (
                    <Button size="m" onClick={processCampaign}>
                        {t(status)}
                    </Button>
                ) : null}
                <Button
                    size="m"
                    color="secondary"
                    as={Link}
                    to={`/promotion?campaign-id=${company.id}`}
                >
                    {t('Редактировать')}
                </Button>
                {masterReportLink ? (
                    <Button
                        size="m"
                        color="secondary"
                        as={Link}
                        to={masterReportLink}
                        target="_blank"
                    >
                        {t('Мастер отчётов')}
                        <Box ml={1}>
                            <IconNewTab size={16} color="var(--color-primary)" />
                        </Box>
                    </Button>
                ) : null}
                <Menu
                    options={isMobile ? { placement: 'bottom' } : { placement: 'right-end' }}
                    trigger={
                        <Button width="40px" size="m" color="secondary">
                            <IconKebab size={16} color="var(--color-primary)" />
                        </Button>
                    }
                >
                    <MenuItem onClick={openInfo}>
                        <Typography noWrap>{t('Справка')}</Typography>
                    </MenuItem>
                </Menu>
            </ButtonWrapper>
        </StartFlex>
    );
};
